/**
 * Scrubber — Vertical playhead cursor draggable across the timeline
 *
 * Displays "Active at time T" summary in a floating tooltip.
 */
import { useRef, useCallback, useState } from 'react';
import type {
  VehicleSceneTimelineResponse,
} from '../../types/vehicleSceneTimeline';
import {
  msToX,
  xToMs,
  toMs,
  formatTime,
  rangesOverlap,
  type TimeScale,
} from '../../utils/timelineUtils';

interface ScrubberProps {
  scale: TimeScale;
  scrubberMs: number | null;
  onScrub: (ms: number | null) => void;
  data: VehicleSceneTimelineResponse | null;
  totalHeight: number;
}

export default function Scrubber({
  scale,
  scrubberMs,
  onScrub,
  data,
  totalHeight,
}: ScrubberProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!containerRef.current) return;
      setDragging(true);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      onScrub(xToMs(x, scale));
    },
    [scale, onScrub],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, scale.widthPx));
      onScrub(xToMs(x, scale));
    },
    [dragging, scale, onScrub],
  );

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  // Compute "active at T" summary
  const activeSummary = (() => {
    if (!scrubberMs || !data) return null;
    const t = scrubberMs;

    const activeSegment = data.segments.find(s =>
      rangesOverlap(toMs(s.start_ts), toMs(s.end_ts), t, t + 1),
    );
    const activeOrders = data.overlays.orders.filter(o =>
      rangesOverlap(toMs(o.from_ts), toMs(o.to_ts), t, t + 1),
    );
    const activeDrivers = data.overlays.drivers.filter(d =>
      rangesOverlap(toMs(d.from_ts), toMs(d.to_ts), t, t + 1),
    );
    const nearestFuel = data.overlays.fuel.reduce<{
      fuel: typeof data.overlays.fuel[0] | null;
      dist: number;
    }>(
      (best, f) => {
        const mid = (toMs(f.from_ts) + toMs(f.to_ts)) / 2;
        const dist = Math.abs(mid - t);
        return dist < best.dist ? { fuel: f, dist } : best;
      },
      { fuel: null, dist: Infinity },
    );

    return {
      time: formatTime(t),
      movement: activeSegment?.type ?? 'UNKNOWN',
      orders: activeOrders.map(o => o.source_order_key || o.from_entity_id),
      drivers: activeDrivers.map(d => d.source_driver_key || d.from_entity_id),
      nearestFuel: nearestFuel.fuel
        ? `${nearestFuel.fuel.label || nearestFuel.fuel.fuel_event_type} (${Math.round(nearestFuel.dist / 60000)}min away)`
        : null,
    };
  })();

  const x = scrubberMs != null ? msToX(scrubberMs, scale) : null;

  return (
    <div
      ref={containerRef}
      className="vst-scrubber-container"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: scale.widthPx,
        height: totalHeight,
        pointerEvents: 'none',
        zIndex: 20,
      }}
    >
      {x != null && (
        <>
          {/* Vertical line */}
          <div
            className="vst-scrubber-line"
            style={{
              position: 'absolute',
              left: x,
              top: 0,
              width: 1,
              height: totalHeight,
              background: '#f97316',
              pointerEvents: 'auto',
              cursor: 'ew-resize',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
          {/* Handle */}
          <div
            className="vst-scrubber-handle"
            style={{
              position: 'absolute',
              left: x - 6,
              top: 0,
              width: 12,
              height: 12,
              background: '#f97316',
              borderRadius: '50% 50% 50% 0',
              transform: 'rotate(-45deg)',
              pointerEvents: 'auto',
              cursor: 'ew-resize',
            }}
          />
          {/* Summary tooltip */}
          {activeSummary && (
            <div
              className="vst-scrubber-tooltip"
              style={{
                position: 'absolute',
                left: x + 12,
                top: 14,
                pointerEvents: 'none',
              }}
            >
              <div className="vst-tooltip-card">
                <div className="vst-tooltip-time">{activeSummary.time}</div>
                <div className="vst-tooltip-row">
                  <span className="vst-tooltip-label">State:</span>
                  <span>{activeSummary.movement}</span>
                </div>
                {activeSummary.orders.length > 0 && (
                  <div className="vst-tooltip-row">
                    <span className="vst-tooltip-label">Orders:</span>
                    <span>{activeSummary.orders.join(', ')}</span>
                  </div>
                )}
                {activeSummary.drivers.length > 0 && (
                  <div className="vst-tooltip-row">
                    <span className="vst-tooltip-label">Driver:</span>
                    <span>{activeSummary.drivers.join(', ')}</span>
                  </div>
                )}
                {activeSummary.nearestFuel && (
                  <div className="vst-tooltip-row">
                    <span className="vst-tooltip-label">Fuel:</span>
                    <span>{activeSummary.nearestFuel}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
